import { ValidatedFanBuilderLoyaltyCoupon } from "gen/api/customer/service_pb"
import { Product as CatalogProduct } from "gen/catalog/models_pb"
import { Location } from "gen/company/models_pb"
import {
  Contacts_Contact as CustomerContact,
  PurchaseOrder,
} from "gen/customer/models_pb"
import { Timestamp as TimestampPB } from "gen/google/protobuf/timestamp_pb"
import { Inventories, Product } from "gen/product/models_pb"
import { Address } from "gen/proto/address/models_pb"
import { UUID } from "gen/proto/uuid/models_pb"
import { TintColor } from "gen/tintcolor/models_pb"
import {
  Sale_Status as SaleStatus,
  Sale_Type as SaleType,
  SaleProduct as SaleProductPB,
} from "gen/txn/models_pb"
import { Decimal } from "shared-ts/classes/decimal"
import { Money } from "shared-ts/classes/money"
import { BaseOrderStatus, OrderShipmentStatus } from "shared-ts/types/orders"
import { OptionType } from "shared-ts/types/tender/tender.types"

import { DisplayableTenderSubmissionMetadata } from "components/composite/TenderDrawer/makeTenderSubmissionMetadata"
import { TransactionProduct } from "features/pos/transactions/transactionProduct"

import { Fulfillment } from "../FulfillmentLocation"
import { Discount, RestockFee } from "../salesSlice"
import { ValueTypes } from "../SaleTotalCostFields/ValueTypeInput"

// ChosenDepositValue is the user's deposit choice from the edit deposit modal. It is
// initialized on the first SyncCartPrices response based on the sale's stored
// deposit or company default.
export type ChosenDepositValue =
  | { type: typeof ValueTypes.PERCENTAGE; percentage: Decimal }
  | { type: typeof ValueTypes.DOLLARS; amount: Money }

export type SaleProduct = SaleProductPB

interface LinkedOrderShipmentBase {
  id: UUID
  identifier: string
  quantity: Decimal
}

export interface LinkedOrder extends LinkedOrderShipmentBase {
  status: BaseOrderStatus
  quantityReceived: Decimal
}

export interface LinkedShipment extends LinkedOrderShipmentBase {
  status: OrderShipmentStatus
}
export interface SaleProductWithOrderInfo extends SaleProduct {
  linkedOrders: LinkedOrder[]
  linkedShipments: LinkedShipment[]
}

export interface SaleLink {
  id: UUID
  type: SaleType
  identifier: string
  saleStatus?: SaleStatus
  // Completed date, so two returns of one line can be told apart.
  soldAt?: TimestampPB
}

export default interface SaleForm {
  idempotencyKey?: string
  customerId?: UUID
  address?: Address
  purchaseOrder?: PurchaseOrder
  customerContact?: CustomerContact
  location?: Location
  fulfillment?: Fulfillment
  designatedClerkId?: UUID | null
  saleNotes?: string
  internalSaleNotes?: string
  /** Fan Builder coupons on the cart; part of the sale so adding one counts as an edit. */
  fanBuilderCoupons?: ValidatedFanBuilderLoyaltyCoupon[]
  quoteExpiration?: Date // string?

  searchProduct?: TransactionProduct
  addProductQuantity?: Decimal
  inventoriesByProductId?: Record<string, Inventories>
  saleProducts?: SaleProduct[]

  productsById?: Record<string, Product>
  tintColorsById?: Record<string, TintColor>
  catalogProductsById?: Record<string, CatalogProduct>

  returnedFromSalesById?: Record<string, SaleLink>
  returnedInSalesBySaleProductId?: Record<string, SaleLink[]>

  // Synced with TenderDrawer state. Used to determine surcharge
  tenderOption?: OptionType
  isSplitPayment?: boolean
  splitAmount?: Money
  splitPayments?: DisplayableTenderSubmissionMetadata[]
  taxRateIds?: UUID[]
  defaultTaxRateId?: UUID
  isCardSurchargeExempt?: boolean
  restockFee?: RestockFee
  additionalDeposit?: Money
  initialDeposit?: Money
  discount?: Discount
}

export const TransactionType = {
  SPECIAL_ORDER: "special-order",
  ZERO_DOLLAR_SALE: "zero-dollar-sale",
  RETURN: "return",
  SALE: "sale",
} as const
export type TransactionTypes =
  (typeof TransactionType)[keyof typeof TransactionType]

export const ActionGroupName = {
  QUOTE: "Quote",
  WILL_CALL: "Will call",
  SAVE: "Save",
  SALE: "Sale",
  COMPLETE_SALE: "Complete sale",
  DEPOSIT: "Deposit",
  REFUND_DEPOSIT: "Refund deposit",
  RETURN: "Return",
  VOID: "Void",
  CLOSE_OUT: "Close out",
  SPECIAL_ORDER: "Special order",
} as const
export type ActionGroupNames =
  (typeof ActionGroupName)[keyof typeof ActionGroupName]

export const SaleActionTabName = {
  QUOTE: "QUOTE",
  WILL_CALL: "WILL_CALL",
  SALE: "SALE",
} as const

export type SaleActionTabNames =
  (typeof SaleActionTabName)[keyof typeof SaleActionTabName]

export const SpecialOrderTabName = {
  SALE: ActionGroupName.SALE,
  DEPOSIT: ActionGroupName.DEPOSIT,
  REFUND_DEPOSIT: ActionGroupName.REFUND_DEPOSIT,
  RETURN: ActionGroupName.RETURN,
  VOID: ActionGroupName.VOID,
  CLOSE_OUT: ActionGroupName.CLOSE_OUT,
  SAVE: ActionGroupName.SAVE,
  WILL_CALL: ActionGroupName.WILL_CALL,
} as const

export type SpecialOrderTabNames =
  (typeof SpecialOrderTabName)[keyof typeof SpecialOrderTabName]

export const SpecialOrderTableMode = {
  VIEWING: "viewing",
  CLOSEOUT: "closeout",
  EDIT: "edit",
} as const

export type SpecialOrderTableModes =
  (typeof SpecialOrderTableMode)[keyof typeof SpecialOrderTableMode]

export const RestockFeeTypes = {
  PERCENTAGE: "Percentage",
  FIXED: "Fixed Amount",
} as const

export type RestockFeeType =
  (typeof RestockFeeTypes)[keyof typeof RestockFeeTypes]

export const RestockFeeObjectType = {
  RATE: "rate",
  FIXED: "fixed",
} as const

export type RestockFeeObjectTypes =
  (typeof RestockFeeObjectType)[keyof typeof RestockFeeObjectType]
